import { html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { when } from 'lit/directives/when.js';
import { repeat } from 'lit/directives/repeat.js';
import { consume } from '@lit/context';
import {
  publicLedgerServiceContext,
  nsLoaderContext,
  nsSettingsServiceContext,
} from '../../../../../ns-services/contexts.js';

import { nsLayoutStyles } from '../../../../../ns-styles/index.js';

import { NSSettingsService, SettingsObject } from '../../../../../ns-settings/ns-settings-service.js';
import { PublicLedgerService } from '../../../../../ns-services/ns-public-ledger-service.js';
import { NSLoader } from '../../../../../ns-services/ns-loader-service.js';
import { callLambda } from '../../../../../helpers/lambda.js';

import Company, { CompaniesRegistrar } from '../../../../../models/company.js';
import Workflow from '../../../../../models/workflow.js';
import JobSchema from '../../../../../models/job-schema.js';

import NSJobComponent from '../../../job.js';

import '@awesome.me/webawesome/dist/components/radio-group/radio-group.js';
import '@awesome.me/webawesome/dist/components/radio/radio.js';
import type WaRadioGroup from '@awesome.me/webawesome/dist/components/radio-group/radio-group.js';

import '../../../../../ns-elements/ns-sign-button.js';
import '../../../../../ns-elements/ns-warning-tip.js';

import { jobErrorStore } from '../../../../../ns-services/ns-job-error-store.js';
import '../../../../../ns-elements/ns-job-layout.js';
import '../../../../../ns-elements/ns-env-picker.js';
import '../../../../../ns-elements/ns-registrar-mismatch-warning.js';
import '../../../../../ns-elements/ns-registrar-record.js';
import '../../../../../ns-elements/ns-test-credentials.js';
import { canPickEnvironment, defaultEnvironment } from '../../../../../helpers/app-mode.js';
import '@awesome.me/webawesome/dist/components/button/button.js';
import definition from '@job-schemas/company/companies-house/validation.json';
import { jobSchema } from '../../../job-schema.js';
import { isRegistrar } from '../../../../../helpers/registrars.js';
import { rememberOAuthStart } from '../../../../../views/oauth/oauth-landing.js';

export interface CHJobError {
  message: string;
  chCode: string | null;
}

type FakeTurnoverMode = 'NONE' | 'FULL_FIGURE' | 'UNRESOLVED_FILING';

@customElement(NSCompanyCHValidation.is)
export default class NSCompanyCHValidation extends NSJobComponent {
  static get is() {
    return 'ns-company-ch-validation';
  }

  @consume({ context: publicLedgerServiceContext })
  private __pLedgerService?: PublicLedgerService;

  @consume({ context: nsSettingsServiceContext })
  private __settings?: NSSettingsService;

  @consume({ context: nsLoaderContext })
  private __loader?: NSLoader;

  private __company?: Company;

  private __companyHouseRegistrar?: CompaniesRegistrar;

  private __companyNumber?: string = '86288198';

  private __workflow?: Workflow;

  private __companiesHouseEntity: any;

  @state()
  private __fakeTurnoverMode: FakeTurnoverMode = 'NONE';

  @state()
  private __appMode?: string;

  @state()
  private __selectedEnvMode: string = 'LIVE';

  @state()
  private __chVerified: boolean = false;

  @state()
  private __companyDetailsMatch: boolean = true;

  static styles = [
    nsLayoutStyles,
    ...super.styles,
    css`
      ns-job-layout {
        --ns-job-layout-image-size: contain;
        --ns-job-layout-image-position: top;
      }

      .value {
        padding: var(--wa-space-2xs) var(--wa-space-xs);
        border-radius: var(--wa-border-radius-l);
        background: var(--wa-color-surface-default);
      }
      .digit {
        width: 30px;
        height: 30px;
      }
    `,
  ];

  static get schema(): JobSchema {
    return jobSchema(definition, { outcomes: { complete: { type: 'hidden' } } });
  }

  async connectedCallback() {
    await super.connectedCallback();
    await this.updateComplete;

    if (!this._db) throw new Error('Unable to find db service');

    if (!this._directory) throw new Error('Unable to find NSDirectoryService');

    if (!this._auth) throw new Error('Unable to find auth service');

    if (!this.__pLedgerService) throw new Error('Unable to find public ledger service');

    if (!this.__settings) throw new Error('Unable to find settings service');

    if (!this.__loader) throw new Error('Loader service not found');

    if (!this._job) throw new Error('No job available');

    if (!this._job.companyId) throw new Error('Could not find company linked to this company validation job');

    if (!this._job.workflow || !this._job.workflow.id) throw new Error('Missing required job workflow ID');
    this.__workflow = await this._db.getById('workflow', this._job.workflow.id);
    if (!this.__workflow) throw new Error(`Could not fetch a workflow with ID: ${this._job.workflow.id}`);

    this.__company = await this.loadJobCompany();

    // App settings — must run before __companyNumber is computed below, since that computation
    // reads __selectedEnvMode.
    const app = this.__settings.settings.app as SettingsObject;
    if (!app) throw new Error('Unable to find app settings');
    this.__appMode = app.mode as string;
    this.__selectedEnvMode = defaultEnvironment(this.__appMode, 'LIVE', 'SANDBOX');

    this.__companyHouseRegistrar = this.__company.companiesRegistrar.find((cr) => isRegistrar(cr, 'COMPANIES_HOUSE'));
    this.__companyNumber =
      this.__companyHouseRegistrar && this.__selectedEnvMode === 'LIVE'
        ? this.__companyHouseRegistrar.id
        : this.__companyNumber;

    await this.__getCallbackError();

    const chCompany = this._job.metadata.find((m) => m.key === 'CH_COMPANY');
    if (!chCompany || !chCompany.value) return;
    this.__companiesHouseEntity = JSON.parse(chCompany.value);
    this.__companyDetailsMatch = this.__doesCompanyDetailsMatch();

    this.__chVerified = true;
    this.openCompletionOverlay({
      title: 'Companies House Connection complete',
      paragraphs: [
        `Your company is now linked to Companies House. Official registration details are verified automatically,
          ensuring workflows and collaborations are tied to a recognised legal entity.`,
        'This reduces manual checks and builds confidence across every project.',
      ],
    });
  }

  private __doesCompanyDetailsMatch(): boolean {
    if (!this.__company) return true;

    const registrationEntity = this.__company.companiesRegistrar.find((cr) => cr.registrarType === 'REGISTRATION');
    if (!registrationEntity) return true;

    const ch = this.__companiesHouseEntity;

    const addressFieldsMatch = (['address1', 'address2', 'city', 'county', 'postcode', 'country'] as const).every(
      (field) => (ch.registeredAddress?.[field] ?? '') === (registrationEntity.registeredAddress?.[field] ?? ''),
    );

    return (
      ch.id === registrationEntity.id &&
      ch.companyName === registrationEntity.companyName &&
      ch.status === registrationEntity.status &&
      ch.type === registrationEntity.type &&
      new Date(ch.incorporatedOn).getTime() === new Date(registrationEntity.incorporatedOn).getTime() &&
      addressFieldsMatch
    );
  }

  private async __getCallbackError() {
    if (!this._job) throw new Error('Missing job');

    const error = jobErrorStore.get<CHJobError>(this._job.id);
    if (error === undefined) return;

    this.triggerCardError(error.message);
  }

  private async __companiesHouseLogin() {
    if (!this._auth) throw new Error('Missing auth service');
    if (!this.__loader) throw new Error('Missing loader service');
    if (!this.__companyNumber) throw new Error('Missing company number');
    if (!this._job) throw new Error('Missing job');
    try {
      this.clearError();
      await this.__loader.startLoading();

      const LambdaResponse = await callLambda(
        'companies-house/auth',
        'POST',
        this._auth.token,
        JSON.stringify({
          companyNumber: this.__companyNumber,
          jobId: this._job.id,
          selectedEnv: this.__selectedEnvMode,
          fakeTurnoverData: this.__fakeTurnoverMode === 'FULL_FIGURE',
          fakeUnresolvedFiling: this.__fakeTurnoverMode === 'UNRESOLVED_FILING',
        }),
      );
      const res = LambdaResponse.res as unknown as { url: string };
      // A declined sign-in comes back to this card (view-oauth)
      rememberOAuthStart('companies-house', this._job.id);
      window.location.href = res.url;
    } catch (err) {
      this.__loader.finishLoading();
      this.triggerCardError(err instanceof Error ? err.message : 'Unable to start Companies House sign-in');
    }
  }

  private async __completeCHVerification() {
    try {
      this.loading('Validating company with companies house and creating a public ledger message');

      if (!this._db) throw new Error('Missing db service');
      if (!this._auth) throw new Error('Missing auth service');
      if (!this._directory || !this._directory.me) throw new Error('Missing directory service');
      if (!this.__company) throw new Error('Missing nodeStream company data');
      if (!this.__workflow || !this.__workflow.parentId) throw new Error('Missing required job workflow parent ID');
      if (!this.__companiesHouseEntity) throw new Error('Missing company house entity info');
      if (!this._job) throw new Error('Missing job info');

      this.clearError();

      await this.recordLedgerValidation({
        company: this.__company,
        lambda: 'companies-house/add-company',
      });

      this.loaded();
      this.outcome('complete');
    } catch (err: unknown) {
      this.loaded();
      this.failJob(err, 'Unexpected error occurred while loading companies');
    }
  }

  private __renderCHCompany() {
    const address = Object.values(this.__companiesHouseEntity.registeredAddress)
      .filter((v) => v !== this.__companiesHouseEntity.registeredAddress.postcode)
      .filter((item, index, arr) => arr.indexOf(item) === index)
      .join(', ');
    const date = new Date(this.__companiesHouseEntity.incorporatedOn);
    const day = date.getUTCDate();
    const month = date.getUTCMonth() + 1;
    const year = date.getUTCFullYear();
    return html`
      <div class="job-panel">
        <ns-registrar-record
          heading="Companies House Data"
          intro="Below is the record retrieved from the Companies House. This data reflects the current state of your organisation on Companies House"
          .fields=${[
            { label: 'Registered Company No:', value: this.__companiesHouseEntity.id },
            { label: 'Registered Company Name:', value: this.__companiesHouseEntity.companyName },
            { label: 'Registered Address:', value: address },
            { label: 'Registered Post Code:', value: this.__companiesHouseEntity.registeredAddress.postcode },
            { label: 'Company Status:', value: this.__companiesHouseEntity.status },
            { label: 'Company Type:', value: this.__companiesHouseEntity.type },
          ]}
        >
          <ns-registrar-mismatch-warning
            slot="status"
            registrar="Companies House"
            ?hidden=${this.__companyDetailsMatch}
          ></ns-registrar-mismatch-warning>
          <div class="layout vertical mb-2">
            <div class="layout horizontal center-center" role="group" aria-labelledby="ch-incorporation-date-label">
              <div class="label small mr-1" id="ch-incorporation-date-label">Company Incorporation Date:</div>
              <ns-input-field
                class="mr-5"
                disabled
                value="${day}"
                width="45"
                type="number"
                animationType="SLIDE_REVEAL"
                label="Day"
                hideLabel
              ></ns-input-field>
              <ns-input-field
                class="mr-5"
                disabled
                value="${month}"
                width="45"
                animationType="SLIDE_REVEAL"
                type="number"
                label="Month"
                hideLabel
              ></ns-input-field>
              <ns-input-field
                disabled
                value="${year}"
                animationType="SLIDE_REVEAL"
                width="60"
                type="number"
                label="Year"
                hideLabel
              ></ns-input-field>
              <div class="flex"></div>
              <div class="flex"></div>
            </div>
          </div>
        </ns-registrar-record>
      </div>
    `;
  }

  private __selectEnv(e: CustomEvent<{ value: string }>) {
    this.__selectedEnvMode = e.detail.value;
    this.__companyNumber = this.__selectedEnvMode === 'LIVE' ? this.__companyHouseRegistrar?.id : '86288198';
  }

  private __renderEnvForm() {
    return html`
      <ns-env-picker
        service="Companies House"
        live-label="Live Companies House API"
        sandbox-label="Sandbox Companies House API"
        .value=${this.__selectedEnvMode}
        @env-change=${this.__selectEnv}
      ></ns-env-picker>
    `;
  }

  private __renderFakeTurnoverToggle() {
    return html`
      <wa-radio-group
        label="Testing"
        .value=${this.__fakeTurnoverMode}
        @change=${(e: Event) => (this.__fakeTurnoverMode = (e.target as WaRadioGroup).value as FakeTurnoverMode)}
      >
        <wa-radio value="NONE">Use a real Companies House filing lookup</wa-radio>
        <wa-radio value="FULL_FIGURE">Simulate a full turnover figure being found</wa-radio>
        <wa-radio value="UNRESOLVED_FILING">
          Simulate a filing being found but not readable (unresolved filing)
        </wa-radio>
      </wa-radio-group>
    `;
  }

  private __renderTestData() {
    return html`
      <div class="job-panel">
        <ns-test-credentials
          .rows=${[
            { label: 'Email Address', value: 'developer@wearelighten.co.uk' },
            { label: 'Password', value: '!Nisltros#6456' },
            { label: 'Company Number', value: '86288198' },
            { label: 'Auth Code', value: '741754' },
          ]}
        >
          <div class="mb-1">The below are the test credentials for Companies House</div>
        </ns-test-credentials>
      </div>
    `;
  }

  renderContent() {
    const companyNumberArr = this.__companyNumber ? [...this.__companyNumber] : [];
    return when(
      !this.__companiesHouseEntity,
      () => html`
        <div class="job-panel">
          ${canPickEnvironment(this.__appMode) ? this.__renderEnvForm() : nothing}
          ${this.__selectedEnvMode === 'SANDBOX' ? this.__renderFakeTurnoverToggle() : nothing}
          <div class="layout horizontal center">
            <div class="label mr-1">Company Name:</div>
            <div class="value small flex">${this.__company?.name}</div>
          </div>
          <div class="layout horizontal center">
            <div class="label mr-1">Company Number:</div>
            ${repeat(
              companyNumberArr,
              (num: string, idx: number) => html`
                <div
                  class="layout vertical center-center value digit small ${companyNumberArr.length === idx + 1
                    ? ''
                    : 'mr-1'}"
                >
                  ${num}
                </div>
              `,
            )}
          </div>
          <div>
            <wa-button variant="brand" type="submit" @click="${this.__companiesHouseLogin}">
              Sign in to Companies House
            </wa-button>
          </div>
          <p>You'll be securely redirected to Companies House to approve access</p>
        </div>
        ${this.__selectedEnvMode === 'SANDBOX' ? this.__renderTestData() : nothing}
      `,
      () => this.__renderCHCompany(),
    );
  }

  render() {
    return html`
      <ns-job-layout image="/images/jobs/companies-house-verification.svg">
        <p slot="intro">
          Linking your organisation to
          <span class="underline">Companies House</span>
          allows nodeStream to verify your business details automatically. This ensures your workflows, transactions and
          collaborations are tied to a recognised legal entity, reducing friction and building trust across.
        </p>
        ${this.renderContent()}
        <div slot="aside" class="primary-info">
          <span class="info-icon" aria-hidden="true">i</span>
          <h3>Verified companies enable smarter collaboration</h3>
          <p>
            By connecting to Companies House, your company information stays accurate and up to date without manual
            checks. It gives partners confidence they're working with a legitimate organisation, simplifies compliance,
            and creates a transparent record of who did what, for whom, and when.
          </p>
          <p class="note">In short: fewer checks, less admin, and clearer accountability on every job.</p>
        </div>
      </ns-job-layout>
    `;
  }

  renderActions() {
    return html`
      <ns-sign-button ?disabled=${!this.__chVerified} @click=${() => this.__completeCHVerification()}></ns-sign-button>
    `;
  }
}
